import { Component, HostListener, ViewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';

import { filter, startWith } from 'rxjs/operators';

import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { CookieService } from 'ngx-cookie-service';

import { UserService } from '@smarthead-api/services/user.service';

import { UiPlatformNotificationComponent } from 'ui-platform';

import { ProfileState } from './profile/state/profile.state';
import { MainNavItemsService } from './shared/core/header/services/main-nav-items.service';
import { AppUpdateService } from './shared/services/app-update.service';
import { DirectionService } from './shared/services/direction.service';
import { FrontendClientAnalyticsService } from './shared/services/frontend-client-analytics.service';
import { NotificationService } from './shared/services/notification.service';
import {
  NOT_FOUND_404,
  ONBOARDING_TYPE_CSAS,
  ONBOARDING_TYPE_SLSP,
  RouterLinkCreatorService,
  SIGNUP_ROUTE
} from './shared/services/router-link-creator.service';
import { WizardService } from './shared/services/wizard.service';
import { AppState } from './shared/state/app.state';
import { isLinkFromOnboarding, isLinkFromOnboradingNewDesign } from './shared/utils/onboarding-utils';

import { LanguageService } from '../../../shared/modules/language/services/language.service';
import { UserLanguageService } from '../../../shared/modules/language/services/user-language.service';
import { environment } from '../environments/environment';

@Component({
  selector: 'sh-root',
  templateUrl: './app.component.html',
  styleUrls: [
    './app.component.scss',
    './app-global-styles.component.scss'
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AppComponent {
  @HostListener('window:scroll', ['$event'])
  scrollListener() {
    this.isScrolled = window.scrollY > 0;
  }

  @ViewChild(UiPlatformNotificationComponent) notification!: UiPlatformNotificationComponent;

  isUserSHAdmin$ = this.store.select(AppState.isUserSHAdmin);
  loggedUserData$ = this.store.select(AppState.getUserData);
  loadedProfile$ = this.store.select(ProfileState.getLoadedProfile);

  appVersion = environment.app_version;
  translateFooterParams = { year: new Date().getFullYear() };

  hideHeader = false;
  hideHeaderSearch = false;
  hideFooter = false;

  isScrolled = false;

  direction: 'ltr' | 'rtl' = 'ltr';
  touLang: 'EN' | 'SK' = this.translocoService.getActiveLang() === 'sk' ? 'SK' : 'EN'; // language for Terms of Use
  showOnboardingHeader = false; // TODO: Refactor headers
  isSLSP = false;
  isCSAS = false;
  readonly slspLogoUrl = 'https://www.explore.besmarthead.com/smarthead-slovenska-sporitelna-esg-nastroj-pre-male-stredne-firmy';
  readonly csasLogoUrl = 'https://business.besmarthead.com/cs/csas-esg';

  constructor(
    protected store: Store,
    private readonly _meta: Meta,
    public router: Router,
    public directionService: DirectionService,
    public cookieService: CookieService,
    public translocoService: TranslocoService,
    public languageService: LanguageService,
    private frontendClientAnalyticsService: FrontendClientAnalyticsService, // do not remove
    private userLanguageService: UserLanguageService, // TODO: do not remove (this instantiate the service, explicit providers won't work for unknown reason)
    public userService: UserService,
    public routerLinkCreatorService: RouterLinkCreatorService,
    public items: MainNavItemsService,
    private readonly wizardService: WizardService,
    private readonly appUpdateService: AppUpdateService,
    private readonly notificationService: NotificationService
  ) {
    this.notificationService.showNotification$.pipe(filter(Boolean), takeUntilDestroyed()).subscribe(() => {
      this.notification.showNotification('', 'general.new-version-update.description', 'bottomLeft', 0, 'general.reload', () => document.location.reload());
    });

    this.appUpdateService.checkForUpdates();

    this._meta.updateTag({
      name: 'og:locale',
      content: this.translocoService.getActiveLang() === 'gb' ? 'en-US' : this.translocoService.getActiveLang()
    });

    this.translocoService.langChanges$.pipe(takeUntilDestroyed()).subscribe(lang => {
      // set locale in metatags
      this._meta.updateTag({
        name: 'og:locale',
        content: lang === 'gb' ? 'en-US' : lang
      });

      this.touLang = lang === 'sk' ? 'SK' : 'EN';
    });

    this.router.events
      .pipe(
        startWith(this.router.url),
        filter(routeEvent => routeEvent instanceof NavigationEnd || typeof routeEvent === 'string'),
        takeUntilDestroyed()
      )
      .subscribe(routeEvent => {
        const route = typeof routeEvent === 'string' ? routeEvent : (routeEvent as NavigationEnd).url;

        this.#setHideHeaderAndFooter(route);
        this.#setHideSearch(route);
        this.isSLSP = route.includes(`/${SIGNUP_ROUTE}/${ONBOARDING_TYPE_SLSP}`);
        this.isCSAS = route.includes(`/${SIGNUP_ROUTE}/${ONBOARDING_TYPE_CSAS}`);
      });

    this.directionService.direction$.pipe(takeUntilDestroyed()).subscribe(direction => {
      this.direction = direction;
    });

    // TODO: Remove when possible
    // Temporary solution until BE is done
    // If the profile already went through the old wizard we will set all the new wizards as done
    this.loadedProfile$
      ?.pipe(
        filter(loadedProfile => loadedProfile !== null && loadedProfile.wizardClose === true),
        takeUntilDestroyed()
      )
      .subscribe(() => this.wizardService.setAllWizardsAsDone());
  }

  #setHideHeaderAndFooter(url: string): void {
    this.showOnboardingHeader = isLinkFromOnboradingNewDesign(url);

    const isOnOnboarding = isLinkFromOnboarding(url);
    this.hideHeader = isOnOnboarding;
  }

  #setHideSearch(url: string): void {
    this.hideHeaderSearch = url.indexOf(NOT_FOUND_404) > 0;
  }
}
